import { ChevronDown } from 'lucide-react'

import { FilterPopoverPanel } from '@/components/filters/filter-dropdown-shell'
import type { FilterOption } from '@/components/filters/filter-menu'
import { VISIBLE_FILTER_FIELD_CONTROL_CLASS, FILTER_POPOVER_CONTENT_CLASS } from '@/components/filters/filter-styles'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  formatDateRangeDisplay,
  DateRangeControl,
  type DateRange,
  type DateRangeMode,
} from '@/lib/list-filters/date-range'
import { cn } from '@/lib/utils'

/** Always-visible single-select for Visible Fields (casual lists). */
export function VisibleEnumSelect({
  value,
  options,
  onValueChange,
  placeholder = 'All',
}: {
  value: string
  options: FilterOption[]
  onValueChange: (value: string) => void
  placeholder?: string
}) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger className={cn(VISIBLE_FILTER_FIELD_CONTROL_CLASS, 'w-full')}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

/** Date / date-range control with preset popover — not a filter-dimension pill. */
export function VisibleDateRangeField({
  value,
  onChange,
  mode,
}: {
  value: DateRange
  onChange: (next: DateRange) => void
  mode: DateRangeMode
}) {
  const label = formatDateRangeDisplay(value, mode)
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          fullWidth
          justify="start"
          className={cn(VISIBLE_FILTER_FIELD_CONTROL_CLASS, 'justify-between font-normal')}
        >
          <span className="truncate">{label}</span>
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className={cn(
          'w-auto min-w-[16rem] max-w-[min(100vw-2rem,24rem)] p-0',
          FILTER_POPOVER_CONTENT_CLASS,
        )}
      >
        <FilterPopoverPanel>
          <DateRangeControl mode={mode} value={value} onChange={onChange} />
        </FilterPopoverPanel>
      </PopoverContent>
    </Popover>
  )
}

export function VisibleTextField({
  value,
  onValueChange,
  placeholder,
}: {
  value: string
  onValueChange: (value: string) => void
  placeholder?: string
}) {
  return (
    <Input
      type="text"
      value={value}
      onChange={(e) => onValueChange(e.target.value)}
      placeholder={placeholder}
      className={VISIBLE_FILTER_FIELD_CONTROL_CLASS}
      autoComplete="off"
    />
  )
}
